import { useEffect, useState } from "react";
import { AllSelectedCheckStates, UseTableOutputType, useTablePropType } from "../types";
import useTablePagination from "./useTablePagination";
import useTableSelect from "./useTableSelect";
import useTableSort from "./useTableSort";

const useTable = (props: useTablePropType): UseTableOutputType => {
    const {
        data = [],
        columns,
        updateTableData,
        useSort = true,
        /** Sorting */
        onSortFunction,
        sortBy,
        sortOrder = 'none',
        customSortingFunction,
        /** Selectable */
        uniqueIdAccessor = '',
        selectAccessor = '',
        labeledByAccessor = '',
        onSelectAll,
        onSelectRow,
        /** Pagination */
        totalRecords,
        pageSizeOptions = [10, 20, 30, 50],
        pageSize = 10,
        page = 1,
        onPageChange,
        onPageSizeChange,
        manual,
        selectAllState,
        showPagination = true,
        ...rest
    } = props;

    const totalRecordsValue = totalRecords ?? data.length;
    const useSelectable = uniqueIdAccessor && selectAccessor && labeledByAccessor;

    const [sas, setSas] = useState<AllSelectedCheckStates>('unchecked'); // only this states, 'checked' | 'unchecked' | 'indeterminate'
    // const [data, setdata] = useState(latestData); // this is mock data with 'isSelect' added

    useEffect(() => {
        setSas(selectAllState ?? 'unchecked');
    }, [selectAllState])

    let pagination, sorting = { data: data }, selectable;

    if (useSort) {
        sorting = useTableSort({
            data: data,
            sortBy,
            sortOrder,
            customSortingFunction,
            onSortFunction,
            manual
        });
    }

    if (useSelectable) {
        selectable = useTableSelect({
            data: sorting.data,
            columns: columns as any,
            /** Required: uniqueIdAccessor, it should be the unique value key, like id, username etc, using this value table will identify the row, it it duplicate then it will not work properly */
            uniqueIdAccessor,
            // Required: selectAccessor, It is expected data should have 'isSelect' or your peffered name,
            selectAccessor,
            // Required: labeledByAccessor, It is important for accessibility, the checkbox need to labeled by some element, or you can use getLabeledById 
            labeledByAccessor,
            selectAllState: sas,
            onSelectAll: (checkedData, event) => {
                const { updated, selectAllState } = checkedData;
                updateTableData && updateTableData(updated);// updated is the updated data content
                if (!manual) {
                    setSas(selectAllState) // selectAllState is the state for select all checkbox, always need to update this
                }
                onSelectAll && onSelectAll(checkedData, event);
            }, // this event will occur when select all is clicked
            onSelectRow: (checkedData, event) => {
                const { updated, selectAllState } = checkedData;
                updateTableData && updateTableData(updated); // updated is the updated data content
                if (!manual) {
                    setSas(selectAllState)// selectAllState is the state for select all checkbox, always need to update this
                }
                onSelectRow && onSelectRow(checkedData, event);
            }
        });
    }

    if (showPagination) {
        pagination = useTablePagination({
            data: sorting.data,
            totalRecords: totalRecordsValue,
            pageSizeOptions,
            pageSize,
            page,
            onPageChange,
            onPageSizeChange,
            manual
        });
    }

    let manualData = {};

    // If manual=true then just by passing data handling from all hooks and sending the original data
    if (manual) {
        manualData = {
            data,
            sortOrder,
            sortBy
        }
    }

    return {
        ...rest,
        columns,
        ...sorting,
        ...selectable,
        ...pagination,
        ...manualData,
        showPagination
    }
}

export default useTable;
